import type { MultilingualQuestion } from '../../types/multilingualQuestion';

// Multilingual version of Weather Conditions questions
export const weatherConditionsMultilingual: MultilingualQuestion[] = [
  {
    id: 'q-wc-1',
    text: {
      en: 'You are driving in heavy rain. What should you do?',
      nl: 'Je rijdt in zware regen. Wat moet je doen?',
      ar: 'أنت تقود في أمطار غزيرة. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-1o1',
        text: {
          en: 'Reduce speed and increase following distance',
          nl: 'Verminder snelheid en vergroot volgafstand',
          ar: 'قلل السرعة وزاد مسافة المتابعة'
        }
      },
      {
        id: 'q-wc-1o2',
        text: {
          en: 'Continue driving at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر في القيادة بسرعة عادية'
        }
      },
      {
        id: 'q-wc-1o3',
        text: {
          en: 'Speed up to get through the rain quickly',
          nl: 'Versnel om snel door de regen te komen',
          ar: 'اسرع للعبور بسرعة عبر المطر'
        }
      }
    ],
    correctAnswerId: 'q-wc-1o1',
    explanation: {
      en: 'In heavy rain, reduce your speed and increase your following distance to account for reduced visibility and longer stopping distances.',
      nl: 'In zware regen moet je je snelheid verminderen en je volgafstand vergroten vanwege verminderde zichtbaarheid en langere remafstanden.',
      ar: 'في الأمطار الغزيرة، قلل سرعتك وزاد مسافة المتابعة لمراعاة انخفاض الرؤية ومسافات التوقف الأطول.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-2',
    text: {
      en: 'You are driving in fog. What should you do?',
      nl: 'Je rijdt in mist. Wat moet je doen?',
      ar: 'أنت تقود في ضباب. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-2o1',
        text: {
          en: 'Use low beam headlights and reduce speed',
          nl: 'Gebruik dimlichten en verminder snelheid',
          ar: 'استخدم المصابيح المنخفضة وقلل السرعة'
        }
      },
      {
        id: 'q-wc-2o2',
        text: {
          en: 'Use high beam headlights for better visibility',
          nl: 'Gebruik grootlichten voor beter zicht',
          ar: 'استخدم المصابيح العالية لرؤية أفضل'
        }
      },
      {
        id: 'q-wc-2o3',
        text: {
          en: 'Continue driving at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر في القيادة بسرعة عادية'
        }
      }
    ],
    correctAnswerId: 'q-wc-2o1',
    explanation: {
      en: 'In fog, use low beam headlights (high beams reflect off fog and reduce visibility) and reduce your speed significantly.',
      nl: 'In mist gebruik je dimlichten (grootlichten reflecteren op mist en verminderen de zichtbaarheid) en verminder je je snelheid aanzienlijk.',
      ar: 'في الضباب، استخدم المصابيح المنخفضة (المصابيح العالية تنعكس على الضباب وتقلل الرؤية) وقلل سرعتك بشكل كبير.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-3',
    text: {
      en: 'You are driving in snow. What should you do?',
      nl: 'Je rijdt in sneeuw. Wat moet je doen?',
      ar: 'أنت تقود في ثلج. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-3o1',
        text: {
          en: 'Reduce speed and use gentle braking',
          nl: 'Verminder snelheid en gebruik zachte remmen',
          ar: 'قلل السرعة واستخدم الفرملة اللطيفة'
        }
      },
      {
        id: 'q-wc-3o2',
        text: {
          en: 'Continue driving at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر في القيادة بسرعة عادية'
        }
      },
      {
        id: 'q-wc-3o3',
        text: {
          en: 'Speed up to get through the snow quickly',
          nl: 'Versnel om snel door de sneeuw te komen',
          ar: 'اسرع للعبور بسرعة عبر الثلج'
        }
      }
    ],
    correctAnswerId: 'q-wc-3o1',
    explanation: {
      en: 'In snow, reduce your speed and use gentle braking to avoid skidding. Sudden movements can cause loss of control.',
      nl: 'In sneeuw moet je je snelheid verminderen en zachte remmen gebruiken om slippen te voorkomen. Plotselinge bewegingen kunnen verlies van controle veroorzaken.',
      ar: 'في الثلج، قلل سرعتك واستخدم الفرملة اللطيفة لتجنب الانزلاق. الحركات المفاجئة يمكن أن تسبب فقدان السيطرة.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-4',
    text: {
      en: 'You are driving in strong winds. What should you do?',
      nl: 'Je rijdt in sterke wind. Wat moet je doen?',
      ar: 'أنت تقود في رياح قوية. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-4o1',
        text: {
          en: 'Reduce speed and hold the steering wheel firmly',
          nl: 'Verminder snelheid en houd het stuur stevig vast',
          ar: 'قلل السرعة وأمسك عجلة القيادة بقوة'
        }
      },
      {
        id: 'q-wc-4o2',
        text: {
          en: 'Continue driving at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر في القيادة بسرعة عادية'
        }
      },
      {
        id: 'q-wc-4o3',
        text: {
          en: 'Speed up to get through the wind quickly',
          nl: 'Versnel om snel door de wind te komen',
          ar: 'اسرع للعبور بسرعة عبر الرياح'
        }
      }
    ],
    correctAnswerId: 'q-wc-4o1',
    explanation: {
      en: 'In strong winds, reduce your speed and hold the steering wheel firmly to maintain control of your vehicle.',
      nl: 'In sterke wind moet je je snelheid verminderen en het stuur stevig vasthouden om de controle over je voertuig te behouden.',
      ar: 'في الرياح القوية، قلل سرعتك وأمسك عجلة القيادة بقوة للحفاظ على السيطرة على مركبتك.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-5',
    text: {
      en: 'You are driving on a motorway at 100 km/h and it starts raining. What should you do?',
      nl: 'Je rijdt op een snelweg met 100 km/u en het begint te regenen. Wat moet je doen?',
      ar: 'أنت تقود على طريق سريع بسرعة 100 كم/س وبدأ المطر. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-5o1',
        text: {
          en: 'Reduce speed and increase following distance',
          nl: 'Verminder snelheid en vergroot volgafstand',
          ar: 'قلل السرعة وزاد مسافة المتابعة'
        }
      },
      {
        id: 'q-wc-5o2',
        text: {
          en: 'Continue at 100 km/h',
          nl: 'Blijf rijden met 100 km/u',
          ar: 'استمر بسرعة 100 كم/س'
        }
      },
      {
        id: 'q-wc-5o3',
        text: {
          en: 'Speed up to get through the rain quickly',
          nl: 'Versnel om snel door de regen te komen',
          ar: 'اسرع للعبور بسرعة عبر المطر'
        }
      }
    ],
    correctAnswerId: 'q-wc-5o1',
    explanation: {
      en: 'When it starts raining, reduce your speed and increase following distance. Wet roads reduce tire grip and increase stopping distance.',
      nl: 'Wanneer het begint te regenen, verminder je je snelheid en vergroot je je volgafstand. Natte wegen verminderen de bandgrip en vergroten de remafstand.',
      ar: 'عندما يبدأ المطر، قلل سرعتك وزاد مسافة المتابعة. الطرق الرطبة تقلل من قبضة الإطارات وتزيد مسافة التوقف.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-6',
    text: {
      en: 'You are driving in icy conditions. What should you do?',
      nl: 'Je rijdt op ijzige omstandigheden. Wat moet je doen?',
      ar: 'أنت تقود في ظروف جليدية. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-6o1',
        text: {
          en: 'Drive very slowly and use gentle steering and braking',
          nl: 'Rijd zeer langzaam en gebruik zachte stuur- en rembewegingen',
          ar: 'اقود ببطء شديد واستخدم توجيه وفرملة لطيفة'
        }
      },
      {
        id: 'q-wc-6o2',
        text: {
          en: 'Continue at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر بسرعة عادية'
        }
      },
      {
        id: 'q-wc-6o3',
        text: {
          en: 'Brake hard when needed',
          nl: 'Rem hard wanneer nodig',
          ar: 'افرمل بقوة عند الحاجة'
        }
      }
    ],
    correctAnswerId: 'q-wc-6o1',
    explanation: {
      en: 'In icy conditions, drive very slowly and use gentle steering and braking. Sudden movements can cause loss of control.',
      nl: 'Op ijzige omstandigheden moet je zeer langzaam rijden en zachte stuur- en rembewegingen gebruiken. Plotselinge bewegingen kunnen verlies van controle veroorzaken.',
      ar: 'في الظروف الجليدية، اقود ببطء شديد واستخدم توجيه وفرملة لطيفة. الحركات المفاجئة يمكن أن تسبب فقدان السيطرة.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-7',
    imageUrl: 'https://mllxuvubthdkdpsiiurr.supabase.co/storage/v1/object/public/cbr-videos/images/weather-conditions/q-wc-7-standing-water.jpg',
    text: {
      en: 'You are driving in heavy rain and see standing water on the road. What should you do?',
      nl: 'Je rijdt in zware regen en ziet stilstaand water op de weg. Wat moet je doen?',
      ar: 'أنت تقود في أمطار غزيرة وترى ماء راكد على الطريق. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-7o1',
        text: {
          en: 'Reduce speed and avoid the standing water if possible',
          nl: 'Verminder snelheid en vermijd het stilstaande water indien mogelijk',
          ar: 'قلل السرعة وتجنب الماء الراكد إن أمكن'
        }
      },
      {
        id: 'q-wc-7o2',
        text: {
          en: 'Continue at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر بسرعة عادية'
        }
      },
      {
        id: 'q-wc-7o3',
        text: {
          en: 'Speed up to get through quickly',
          nl: 'Versnel om er snel doorheen te komen',
          ar: 'اسرع للعبور بسرعة'
        }
      }
    ],
    correctAnswerId: 'q-wc-7o1',
    explanation: {
      en: 'Standing water can cause aquaplaning. Reduce speed and avoid driving through deep water if possible.',
      nl: 'Stilstaand water kan aquaplaning veroorzaken. Verminder snelheid en vermijd rijden door diep water indien mogelijk.',
      ar: 'الماء الراكد يمكن أن يسبب الانزلاق المائي. قلل السرعة وتجنب القيادة عبر الماء العميق إن أمكن.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-8',
    text: {
      en: 'You are driving in fog and visibility is very poor. What should you do?',
      nl: 'Je rijdt in mist en de zichtbaarheid is zeer slecht. Wat moet je doen?',
      ar: 'أنت تقود في ضباب والرؤية ضعيفة جدا. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-8o1',
        text: {
          en: 'Reduce speed significantly and use low beam headlights',
          nl: 'Verminder snelheid aanzienlijk en gebruik dimlichten',
          ar: 'قلل السرعة بشكل كبير واستخدم المصابيح المنخفضة'
        }
      },
      {
        id: 'q-wc-8o2',
        text: {
          en: 'Use high beam headlights for better visibility',
          nl: 'Gebruik grootlichten voor beter zicht',
          ar: 'استخدم المصابيح العالية لرؤية أفضل'
        }
      },
      {
        id: 'q-wc-8o3',
        text: {
          en: 'Continue at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر بسرعة عادية'
        }
      }
    ],
    correctAnswerId: 'q-wc-8o1',
    explanation: {
      en: 'In poor visibility due to fog, reduce speed significantly and use low beam headlights. High beams reflect off fog and reduce visibility.',
      nl: 'Bij slechte zichtbaarheid door mist moet je je snelheid aanzienlijk verminderen en dimlichten gebruiken. Grootlichten reflecteren op mist en verminderen de zichtbaarheid.',
      ar: 'في الرؤية الضعيفة بسبب الضباب، قلل السرعة بشكل كبير واستخدم المصابيح المنخفضة. المصابيح العالية تنعكس على الضباب وتقلل الرؤية.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-9',
    text: {
      en: 'You are driving in heavy rain and your windshield wipers are on maximum. What should you do?',
      nl: 'Je rijdt in zware regen en je ruitenwissers staan op maximum. Wat moet je doen?',
      ar: 'أنت تقود في أمطار غزيرة ومساحات الزجاج الأمامي على الحد الأقصى. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-9o1',
        text: {
          en: 'Reduce speed and consider stopping if visibility becomes too poor',
          nl: 'Verminder snelheid en overweeg te stoppen als de zichtbaarheid te slecht wordt',
          ar: 'قلل السرعة وفكر في التوقف إذا أصبحت الرؤية ضعيفة جدا'
        }
      },
      {
        id: 'q-wc-9o2',
        text: {
          en: 'Continue at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر بسرعة عادية'
        }
      },
      {
        id: 'q-wc-9o3',
        text: {
          en: 'Speed up to get through the rain quickly',
          nl: 'Versnel om snel door de regen te komen',
          ar: 'اسرع للعبور بسرعة عبر المطر'
        }
      }
    ],
    correctAnswerId: 'q-wc-9o1',
    explanation: {
      en: 'If visibility is poor even with wipers on maximum, reduce speed significantly and consider stopping in a safe place until conditions improve.',
      nl: 'Als de zichtbaarheid slecht is zelfs met ruitenwissers op maximum, verminder je je snelheid aanzienlijk en overweeg je te stoppen op een veilige plaats totdat de omstandigheden verbeteren.',
      ar: 'إذا كانت الرؤية ضعيفة حتى مع المساحات على الحد الأقصى، قلل السرعة بشكل كبير وفكر في التوقف في مكان آمن حتى تتحسن الظروف.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-10',
    text: {
      en: 'You are driving in snow and the road is slippery. What should you do?',
      nl: 'Je rijdt in sneeuw en de weg is glad. Wat moet je doen?',
      ar: 'أنت تقود في ثلج والطريق زلق. ماذا يجب أن تفعل؟'
    },
    options: [
      {
        id: 'q-wc-10o1',
        text: {
          en: 'Drive very slowly, increase following distance, and avoid sudden movements',
          nl: 'Rijd zeer langzaam, vergroot volgafstand en vermijd plotselinge bewegingen',
          ar: 'اقود ببطء شديد، زاد مسافة المتابعة، وتجنب الحركات المفاجئة'
        }
      },
      {
        id: 'q-wc-10o2',
        text: {
          en: 'Continue at normal speed',
          nl: 'Blijf rijden met normale snelheid',
          ar: 'استمر بسرعة عادية'
        }
      },
      {
        id: 'q-wc-10o3',
        text: {
          en: 'Brake hard when needed',
          nl: 'Rem hard wanneer nodig',
          ar: 'افرمل بقوة عند الحاجة'
        }
      }
    ],
    correctAnswerId: 'q-wc-10o1',
    explanation: {
      en: 'On slippery snow-covered roads, drive very slowly, increase following distance, and avoid sudden steering, braking, or acceleration.',
      nl: 'Op gladde sneeuwbedekte wegen moet je zeer langzaam rijden, je volgafstand vergroten en plotselinge stuur-, rem- of versnellingsbewegingen vermijden.',
      ar: 'على الطرق الزلقة المغطاة بالثلج، اقود ببطء شديد، زاد مسافة المتابعة، وتجنب التوجيه أو الفرملة أو التسارع المفاجئ.'
    },
    subject: 'Weather Conditions'
  },
  {
    id: 'q-wc-20',
    text: {
      en: 'How does snow and ice reduce tire grip on the road?',
      nl: 'Hoe verminderen sneeuw en ijs de grip van de banden op de weg?',
      ar: 'كيف تقلل الثلوج والجلد/الثلج المتجمد من تماسك الإطار مع الطريق؟'
    },
    options: [
      {
        id: 'q-wc-20o1',
        text: {
          en: 'It reduces the contact area between tire and road surface',
          nl: 'Het vermindert het contactoppervlak tussen band en wegdek',
          ar: 'إنها تقلل مساحة التلامس بين الإطار وسطح الطريق'
        }
      },
      {
        id: 'q-wc-20o2',
        text: {
          en: 'It has no effect on tire grip',
          nl: 'Het heeft geen effect op de bandengreep',
          ar: 'ليس لها أي تأثير على تماسك الإطار'
        }
      },
      {
        id: 'q-wc-20o3',
        text: {
          en: 'It increases tire grip',
          nl: 'Het vergroot de grip van de banden',
          ar: 'إنها تزيد من تماسك الإطار'
        }
      }
    ],
    correctAnswerId: 'q-wc-20o1',
    explanation: {
      en: 'Snow and ice reduce the contact area between the tire and the road surface, significantly reducing grip. This is why special winter tires are recommended for better grip in cold conditions.',
      nl: 'Sneeuw en ijs verminderen het contactoppervlak tussen de band en het wegdek, waardoor de grip aanzienlijk afneemt. Daarom worden speciale winterbanden aanbevolen voor betere grip in koude omstandigheden.',
      ar: 'تقلل الثلوج والثلج المتجمد مساحة التلامس بين الإطار وسطح الطريق، ما يقلل التماسك بشكل كبير. لهذا السبب تُنصح بإطارات الشتاء الخاصة لتحسين التماسك في الظروف الباردة.'
    },
    subject: 'Weather Conditions'
  }
];

























